import type { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import * as Sentry from "@sentry/react";
import { isStripePublishableKeyConfigured } from "./env";
import { trackEvent } from "@/lib/dataLayer";

/**
 * =====================================================================
 * apps/web/src/lib/stripe/stripe.ts
 * =====================================================================
 * Singleton de Stripe.js SEGURO (promise que NUNCA rechaza).
 *
 * `loadStripe()` RECHAZA con `Error: Failed to load Stripe.js` cuando el
 * `<script src="https://js.stripe.com/v3/">` no carga (caída de red,
 * adblocker, DNS). Ese rechazo provoca unhandled rejections en prod:
 *
 *   - `@stripe/react-stripe-js` v5 deriva sus promises con solo `.then`
 *     y sin `.catch` (parseStripeProp / Elements / useStripe), por lo que
 *     un promise rechazado pasado a `<Elements stripe={...}>` genera
 *     unhandledrejection desde node_modules (Sentry issue
 *     7974a2e8a23d423d8492c2dad2264fd7).
 *   - Cualquier `.then(...)` propio sin `.catch` sobre ese promise.
 *
 * Este helper lo evita: en fallo resuelve a `null` (patrón soportado por
 * react-stripe-js, `validateStripe` acepta explícitamente null) y RESETEA
 * la caché interna para que la siguiente llamada reintente la carga del
 * script (hace funcional el botón "Reintentar" de los modales).
 *
 * El fallo se reporta a Sentry con nivel `warning` (observabilidad de la
 * frecuencia de fallos de red/adblock sin ruido de "unhandled error").
 * =====================================================================
 */
const cache = new Map<string, Promise<Stripe | null>>();

export function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  const cached = cache.get(publishableKey);
  if (cached) return cached;

  let promise: Promise<Stripe | null>;
  if (!isStripePublishableKeyConfigured(publishableKey)) {
    // Sin clave válida no se intenta carga: los modales muestran su propio
    // error de configuración.
    promise = Promise.resolve(null);
  } else {
    promise = loadStripe(publishableKey).catch((err: unknown) => {
      // Reset de caché: permite reintentar la carga en la próxima llamada.
      cache.delete(publishableKey);
      console.warn("[stripe] Failed to load Stripe.js:", err);
      try {
        Sentry.captureException(err, {
          level: "warning",
          tags: { area: "stripe-js" },
        });
      } catch {
        /* SDK no inicializado (dev/tests) */
      }
      // GA4: medir el abandono del funnel por fallo de carga de stripe.js
      // (frecuente en móviles con red inestable o adblock).
      try {
        trackEvent("bs_payment_error", { step: "load_stripe_js", error: "load_failed" });
      } catch {
        /* dataLayer no disponible */
      }
      return null;
    });
  }

  cache.set(publishableKey, promise);
  return promise;
}

/** Solo para tests: limpia el estado entre casos. */
export function __resetStripeCacheForTests(): void {
  cache.clear();
}
